import { format } from 'date-fns';
import fetch from 'isomorphic-fetch';
import qs from 'query-string';
import {
  PROCESS_SALE_FAILURE,
  PROCESS_SALE_REQUEST,
  PROCESS_SALE_SUCCESS,
  PROCESS_SALE_TIMEOUT
} from '../constants/ActionTypes';
import ItemTypes from '../constants/ItemTypes';
import { first } from '../utils';
import addOnKey from '../utils/addOnKey';
import { parseISOLocal } from '../utils/date';
import { checkStatus, dispatchError, parseJSON } from '../utils/fetch';
import isDebitware from '../utils/isDebitware';
import packageKey from '../utils/packageKey';
import { packagePrice } from '../utils/packagePrice';
import { addOnsTotals } from '../utils/totals';

const isEmpty = str => str == null || str.trim() == '';

/**
 * process sale
 * @param {*} sale
 */
function processSaleRequest(sale) {
  return {
    type: PROCESS_SALE_REQUEST,
    sale
  };
}

function processSaleFailure(error, errors) {
  return {
    type: PROCESS_SALE_FAILURE,
    error,
    errors
  };
}

function processSaleSuccess(emailBody) {
  return {
    type: PROCESS_SALE_SUCCESS,
    emailBody,
    date: new Date(),
    timeout: setTimeout(processSaleTimeout, 5000)
  };
}

function processSaleTimeout() {
  return {
    type: PROCESS_SALE_TIMEOUT
  };
}

export default function processSale(token) {
  return (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const config = getState().config;

      const {
        sale: { sale },
        inventory
      } = getState();

      if (token != null && token.id == null) {
        dispatch(processSaleFailure('Credit card could not be verified.'));
        reject();
        return;
      }

      dispatch(processSaleRequest(sale));

      const date = parseISOLocal(sale.day);
      const dateString = format(date, 'yyyyMMdd');

      const inventoryPackages = inventory.packages[packageKey(sale.day)] || [];
      const saleItems = Object.keys(sale.packages).map(pkgId => {
        const pkg = first(inventoryPackages, pkg => pkg.item_id == pkgId);
        if (pkg == null) return;

        let pkgAddOns = [];
        if (sale.packageAddOns[pkgId]) {
          Object.keys(sale.packageAddOns[pkgId]).forEach(addOnId => {
            const pkg = first(inventoryPackages, pkg => pkg.item_id == pkgId);
            if (pkg == null) return;

            const addOns = inventory.addOns[addOnKey(sale.day, pkg)];
            const addOn = first(addOns, addOn => addOn.item_id == addOnId);

            const addOnRes = {
              department: addOn.department,
              category: addOn.category,
              item: addOn.item,
              descrip: addOn.descrip,
              qty: sale.packageAddOns[pkgId][addOnId].qty,
              price: packagePrice(addOn, sale),
              date: dateString,
              time: `${sale.packageAddOns[pkgId][addOnId].time ||
                sale.packages[pkgId].time ||
                '0000'}00`,
              type: getItemType(addOn),
              dw: isDebitware(addOn),
              barcodePrefix: getBarcodePrefix(config, addOn),
              guests: (
                ((sale.packageAddOnGuests || {})[pkgId] || {})[addOnId] || []
              ).map((guest, idx) => {
                return {
                  first_name: guest.first_name,
                  last_name: guest.last_name,
                  email: guest.email_init,
                  isPassholder: idx === 0 && config.isFirstGuestPassholder,
                  birth_date: guest.birth_date,
                  address: {
                    street: guest.address,
                    city: guest.city,
                    region: guest.state,
                    postalCode: guest.zip
                  }
                };
              }),
              special: addOn.special
            };

            if (addOnRes.qty > 0) {
              pkgAddOns.push(addOnRes);
            }
          });
        }

        const emailContentLabel =
          config.itemEmailContent == null
            ? null
            : config.itemEmailContent[pkg.item] == null
            ? null
            : config.itemEmailContent[pkg.item].label || null;
        const emailContentText =
          config.itemEmailContent == null
            ? null
            : config.itemEmailContent[pkg.item] == null
            ? null
            : config.itemEmailContent[pkg.item].text || null;

        // if using a single pass number's benefits for every item,
        // duplicate the retrieved benefits to match item quantity
        const benefitsForItem = [...sale.benefits];
        if (
          config.validateTimedPasses &&
          !config.validateTimedPassesIndividually
        ) {
          for (let index = 0; index < sale.packages[pkgId].qty - 1; index++) {
            benefitsForItem.push(sale.benefits[0]);
          }
        }

        return {
          department: pkg.department,
          category: pkg.category,
          item: pkg.item,
          descrip: pkg.descrip,
          qty: sale.packages[pkgId].qty,
          price: packagePrice(pkg, sale, null, true),
          mods: pkgAddOns,
          date: dateString,
          time: `${sale.packages[pkgId].time || '0000'}00`,
          type: getItemType(pkg),
          dw: isDebitware(pkg),
          barcodePrefix: getBarcodePrefix(config, pkg),
          emailContentLabel,
          emailContentText,
          special: pkg.special,
          benefits: benefitsForItem
        };
      });

      const gcs = sale.giftCards
        .filter(gc => gc != null && gc.number && gc.number.trim().length > 0)
        .map(gc => gc.number);

      var postBody = {
        paymentToken: token != null ? token.id : null,
        giftCard: gcs.length == 0 ? null : gcs.join(','),
        first_name: sale.firstName == null ? '' : sale.firstName.trim(),
        last_name: sale.lastName == null ? '' : sale.lastName.trim(),
        email: sale.email == null ? '' : sale.email.trim(),
        phone: sale.phone == null ? null : sale.phone.trim(),
        address: {
          street: sale.street == null ? null : sale.street.trim(),
          city: sale.city == null ? null : sale.city.trim(),
          region: sale.region == null ? null : sale.region.trim(),
          country: sale.country == null ? null : sale.country.trim(),
          postalCode: sale.postalCode == null ? null : sale.postalCode.trim()
        },
        sale_id: sale.id,
        sale: {
          amount: addOnsTotals(sale, inventory),
          items: saleItems
        },
        noBarcodes: config.noBarcodes,
        allBarcodes: config.allBarcodes,
        hideBarcodeImages: config.hideBarcodeImages,
        applyBenefitsToMods: config.applyBenefitsToMods,
        restrictModsToFirstItem: config.restrictModsToFirstItem,
        emailContent: config.emailContent,
        emailContentLabel: config.emailContentLabel,
        mktgcode: sale.promoCode
      };

      return fetch(
        `${config.apiUrl}/sales/${sale.id}/checkout?${qs.stringify({
          client: config.client
        })}`,
        {
          method: 'post',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(postBody)
        }
      )
        .then(checkStatus)
        .then(parseJSON)
        .then(emailBody => {
          if (typeof config.onCheckout == 'function') {
            setTimeout(() => {
              config.onCheckout(postBody, emailBody);
            }, 1000);
          }
          dispatch(processSaleSuccess(emailBody));
          resolve(emailBody);
        })
        .catch((...args) => {
          dispatchError(dispatch, processSaleFailure)(...args);
          reject(...args);
        });
    });
}

function getBarcodePrefix(config, i) {
  return config.prefixBarcodeWithUserCode
    ? i.user_code != null
      ? i.user_code
      : null
    : null;
}

/**
  As mentioned when we were going over how to determine which items are pass items:
    1. good practice would be to check the item_type field that returns from GetItemExpanded
      a. If the value is 3 it’s a guest pass
      b. If the value is 1 it’s not a guest pass, check the admissions field
        i. If the admissions field value is > 0, it’s an access ticket
  To clarify, both Guest Passes and Access items require barcodes from the GetPassId and GetAccessId calls respectively.

  if I remember correctly the parent item is not actually a ticket, the mods themselves are the ones actually configured as tickets so it flows that you would generate barcodes for each of them rather than the parent item, when applicable
*/
function getItemType(pkg) {
  return pkg.item_type == 3
    ? ItemTypes.GUEST_PASS
    : pkg.item_type == 1 && pkg.admissions > 0
    ? ItemTypes.ACCESS
    : ItemTypes.NONE;
}
